import { useLayoutEffect, useState } from 'react';
import { cloneDeep } from 'lodash';

const MAX_WIDTH_LINE_NAME_CHIP = 120;

type LineWithId = {
  lineId: string;
};

type UseHeaderLinesLayoutArgs<T extends LineWithId> = {
  headerContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  lines: T[];
  currentLineId: string;
  setDisplayedLines: React.Dispatch<React.SetStateAction<T[]>>;
  setDisplayDealLinesDropdown: React.Dispatch<React.SetStateAction<boolean>>;
};

const useHeaderLinesLayout = <T extends LineWithId>({
  headerContainerRef,
  lines,
  currentLineId,
  setDisplayedLines,
  setDisplayDealLinesDropdown,
}: UseHeaderLinesLayoutArgs<T>): void => {
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);

  useLayoutEffect(() => {
    if (!headerContainerRef.current) return;
    const headerWidth = headerContainerRef.current.clientWidth;
    const isOverflowing = lines.length * MAX_WIDTH_LINE_NAME_CHIP >= headerWidth;

    if (isOverflowing) {
      const maxLinesForViewport = Math.floor(headerWidth / MAX_WIDTH_LINE_NAME_CHIP);
      const currentLineIndex = lines.findIndex((line) => line.lineId === currentLineId);

      const numberOfLinesToRemove = lines.length - maxLinesForViewport;
      const startIndex = currentLineIndex < numberOfLinesToRemove ? lines.length - numberOfLinesToRemove : 0;

      const isTrimmingRequired = lines.length - currentLineIndex > maxLinesForViewport;

      if (isTrimmingRequired) {
        const copyOfLines = cloneDeep(lines);
        const updatedLines = copyOfLines.slice(currentLineIndex, currentLineIndex + maxLinesForViewport);
        setDisplayedLines(updatedLines);
      } else {
        const copyOfLines = cloneDeep(lines);
        copyOfLines.splice(startIndex, numberOfLinesToRemove);
        setDisplayedLines(copyOfLines);
      }

      setDisplayDealLinesDropdown(true);
    } else {
      setDisplayedLines(lines);
      setDisplayDealLinesDropdown(false);
    }
  }, [lines, windowWidth, currentLineId]);

  useLayoutEffect(() => {
    const handleResize = (): void => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
};

export default useHeaderLinesLayout;
